/**
 * @file       Preference service for logged in user
 * @version    2.0.0
 * @author     Boris Veits
 * @copyright  Copyright (c) 2018, MindShare HDV LLC.  All rights reserved.
 *             Property of MindShare HDV LLC.  This file cannot be used or
 *             altered in anyway without expressed written permission from
 *             MindShare HDV LLC.
 */

import { Injectable, Injector } from '@angular/core';
import {
    BehaviorSubject,
    catchError,
    map,
    Observable,
    of,
    Subscription
} from 'rxjs';
import {
    HOME_PAGE_VALUE,
    HOME_TAG,
    SETTING_CODE_REF,
    SETTING_CODE_VALUE,
    TWO_STEP_VERIFICATION_VALUE
}
    from '@app/constants/Setting.constant';
import { ERROR } from '@app/constants/Error.constant';
import { PREFERENCE } from '@app/constants/Api.constant';
import { ApiService } from '@app/secure/service/Api.service';
import { SettingService } from '@app/service/setting.service';
import { AuthService } from '@app/service/auth.service';


/**
 * @desc   - Preference interface
 */
export interface Preference
{
    code: string;
    value: string;
}

export type ModuleTextCaseRule = 'lower' | 'upper' | 'capitalize';
export type ModuleTextCase =
    'note' | 'jury' | 'caseMotion' | 'probation' | 'docket';

@Injectable
({
    providedIn: 'root'
})

/**
 * @desc   - Preference service
 */
class PreferenceService
{
    upperCaseEnabled: boolean;
    expandNoteEnabled: boolean;
    userUId: string;
    private settingSubscription: Subscription;

    public preferences$ = new BehaviorSubject <Preference[]>([]);

    private readonly textCasePreferenceCodes: string[] =
    [
        SETTING_CODE_VALUE.NOTE_UPPERCASE,
        SETTING_CODE_VALUE.JURY_UPPERCASE,
        SETTING_CODE_VALUE.CASE_MOTION_UPPERCASE,
        SETTING_CODE_VALUE.PROBATION_UPPERCASE,
        SETTING_CODE_VALUE.DOCKET_UPPERCASE
    ];

//_____________________________ C O N S T R U C T O R ________________________
    /**
     * @desc   - Service constructor
     * @param  - apiService: ApiService - ApiService
     */
    constructor
    (
        private apiService: ApiService,
        private settingService: SettingService,
        private injector: Injector
    )
    {
        let storedPreferences: string;
        let parsed: Preference[];

        storedPreferences = localStorage.getItem('preferences');
        if (storedPreferences)
        {
            try
            {
                parsed = JSON.parse(storedPreferences);
                this.setPreferences(parsed);
            }
            catch (e)
            {
                console.error('Error parsing preferences from localStorage', e);
            }
        }

        this.settingSubscription = this.settingService.settings$
        .subscribe(() =>
        {
            let current: Preference[];
            let allowStorage: boolean;

            current = this.preferences$.value || [];
            allowStorage = this.shouldWritePreferencesToStorage();

            if (allowStorage && current.length > 0)
            {
                this.setPreferences(current);
            }
        });
    }

     //__________________ P R I V A T E  M E T H O D S __________________________

    /**
     * @desc   - allow localStorage only when session may persist and user
     *           is known or login hydrate is in progress (setPreferences runs
     *           before user$.next during hydrate)
     * @access - private
     * @param  -
     * @return - boolean
     */
    private shouldWritePreferencesToStorage
    (
    ): boolean
    {
        let auth: AuthService | null;
        let result: boolean;

        auth = null;
        result = false;
        try
        {
            auth = this.injector.get(AuthService);
        }
        catch (e)
        {
            auth = null;
        }

        if (!auth)
        {
            result = false;
        }
        else if (!auth.mayPersistSessionToBrowser())
        {
            result = false;
        }
        else if (auth.userValue ||
            auth.isUserSessionHydrationInProgress())
        {
            result = true;
        }
        else
        {
            result = false;
        }

        return result;
    }

    private getSystemTextCaseMap(): Map<string, boolean>
    {
        let settingsJson: string;
        let parsedSettings: any;
        let settings: any[];
        let textCaseMap: Map<string, boolean>;
        let noteCaseValue: string;
        let juryCaseValue: string;
        let caseMotionCaseValue: string;
        let probationCaseValue: string;
        let docketCaseValue: string;

        settingsJson = localStorage.getItem('settings');
        parsedSettings = [];
        settings = [];
        textCaseMap = new Map<string, boolean>();

        if (settingsJson)
        {
            try
            {
                parsedSettings = JSON.parse(settingsJson);
                settings =
                    Array.isArray(parsedSettings) ?
                    parsedSettings :
                    parsedSettings?.settings || [];
            }
            catch (error)
            {
                console.error('Error parsing settings from localStorage', error);
            }
        }

        noteCaseValue = settings.find((item: any) =>
            Number(item.code) ===
            SETTING_CODE_REF.NOTE_TEXT_CASE_CODE
        )?.value;
        juryCaseValue = settings.find((item: any) =>
            Number(item.code) ===
            SETTING_CODE_REF.JURY_TEXT_CASE_CODE
        )?.value;
        caseMotionCaseValue = settings.find((item: any) =>
            Number(item.code) ===
            SETTING_CODE_REF.CASE_MOTION_TEXT_CASE_CODE
        )?.value;
        probationCaseValue = settings.find((item: any) =>
            Number(item.code) ===
            SETTING_CODE_REF.PROBATION_TEXT_CASE_CODE
        )?.value;
        docketCaseValue = settings.find((item: any) =>
            Number(item.code) ===
            SETTING_CODE_REF.DOCKET_TEXT_CASE_CODE
        )?.value;

        textCaseMap.set(
            SETTING_CODE_VALUE.NOTE_UPPERCASE,
            noteCaseValue === '1' || noteCaseValue === 'true'
        );
        textCaseMap.set(
            SETTING_CODE_VALUE.JURY_UPPERCASE,
            juryCaseValue === '1' || juryCaseValue === 'true'
        );
        textCaseMap.set(
            SETTING_CODE_VALUE.CASE_MOTION_UPPERCASE,
            caseMotionCaseValue === '1' || caseMotionCaseValue === 'true'
        );
        textCaseMap.set(
            SETTING_CODE_VALUE.PROBATION_UPPERCASE,
            probationCaseValue === '1' || probationCaseValue === 'true'
        );
        textCaseMap.set(
            SETTING_CODE_VALUE.DOCKET_UPPERCASE,
            docketCaseValue === '1' || docketCaseValue === 'true'
        );

        return textCaseMap;
    }

    private resolveTextCasePreferences
    (
        preferences: Preference[]
    ): Preference[]
    {
        let resolvedPreferences: Preference[];
        let textCaseMap: Map<string, boolean>;
        let hasPreference: boolean;
        let isUpperCaseEnabled: boolean;

        resolvedPreferences = Array.isArray(preferences) ?
            [...preferences] :
            [];
        textCaseMap = this.getSystemTextCaseMap();

        this.textCasePreferenceCodes.forEach((code) =>
        {
            hasPreference = resolvedPreferences.some((preference) =>
                preference.code === code
            );

            if (!hasPreference)
            {
                isUpperCaseEnabled = textCaseMap.get(code) === true;

                resolvedPreferences.push(
                {
                    code: code,
                    value: isUpperCaseEnabled ? 'true' : 'false'
                });
            }
        });

        return resolvedPreferences;
    }

    //__________________ P U B L I C  M E T H O D S ____________________________
    /**
     * @desc   - Retrieves the user's preferences
     * @access - public
     * @return - void
     */
    public async loadPreferences
    (
        forceReload: boolean = false
    )
    {
        let data : any;
        let response : any;
        let shouldReload: boolean;
        let preferences: Preference[];
        let skipEmptyOverwrite: boolean;

        try
        {
            shouldReload = false;
            preferences = null;

            shouldReload =
                forceReload ||
                !this.preferences$.value ||
                this.preferences$.value.length == 0;

            if (shouldReload)
            {
                data = {};
                response = await this.apiService.post<any>
                (
                    `${PREFERENCE.GET_PREFERENCE}`,
                    data
                ).toPromise();

                preferences =
                    response?.preferences ||
                    response?.cyData?.preferences ||
                    null;

                if (Array.isArray(preferences))
                {
                    skipEmptyOverwrite =
                        preferences.length === 0 &&
                        this.preferences$.value &&
                        this.preferences$.value.length > 0;

                    if (!skipEmptyOverwrite)
                    {
                        this.setPreferences(preferences);
                    }
                }
            }
        }
        catch (error)
        {
            console.error('+++error in loading preference+++');
        }
    }
    // Another way to load preferences without try/catch
    // public loadPreferences(): Observable<void> {
    //     if (!this.preferences$.value || this.preferences$.value.length === 0) {
    //         return this.apiService.post<any>(PREFERENCE.GET_PREFERENCE, {}).pipe(
    //             map(response => {
    //                 if (response.error === ERROR.NO_ERROR) {
    //                     this.setPreferences(response.cyData.preferences);
    //                 }
    //             }),
    //             catchError(error => {
    //                 console.error('Error loading preferences:', error);
    //                 return EMPTY;  // or return of(null) if you need a value
    //             })
    //         );
    //     }
    //     return of(void 0);
    // }

    /**
     * @desc   - Sets the user's preferences
     * @access - public
     * @param  - preferences: Preference[] - User's preferences
     * @return - void
     */
    public setPreferences
    (
        preferences: Preference[]
    )
    {
        let resolvedPreferences: Preference[];
        let allowStorage: boolean;

        resolvedPreferences = this.resolveTextCasePreferences(preferences);
        allowStorage = this.shouldWritePreferencesToStorage();

        this.preferences$.next(resolvedPreferences);

        if (!allowStorage)
        {
            localStorage.removeItem('preferences');
        }
        else
        {
            try
            {
                if (!resolvedPreferences || resolvedPreferences.length === 0)
                {
                    localStorage.removeItem('preferences');
                }
                else
                {
                    localStorage.setItem(
                        'preferences',
                        JSON.stringify(resolvedPreferences)
                    );
                }
            }
            catch (error)
            {
                console.error('setPreferences localStorage:', error);
            }
        }
    }

    /**
     * @desc   - clear persisted preferences on logout.
     *           Does NOT emit to preferences$ so that components still
     *           mounted during teardown cannot react and fire stale API
     *           calls on an already-invalidated session.  The BehaviorSubject
     *           value is reset on the next successful login via setPreferences.
     * @access - public
     * @param  -
     * @return -
     */
    public clearPersistedStateOnLogout
    (
    )
    {
        try
        {
            localStorage.removeItem('preferences');
        }
        catch (error)
        {
            console.error('clearPersistedStateOnLogout:', error);
        }
    }

    /**
     * @desc   - Retrieves the user's preferences
     * @access - public
     * @return - Preference[] - User's preferences
     */
    public getPreferences()
    {
        let fromMemory: Preference[];
        let preferencesJStr: string | null;
        let result: Preference[];

        fromMemory = this.preferences$.value;
        preferencesJStr = null;
        result = [];

        if (fromMemory && fromMemory.length > 0)
        {
            result = fromMemory;
        }
        else
        {
            try
            {
                preferencesJStr = localStorage.getItem('preferences');
                if (preferencesJStr)
                {
                    this.setPreferences(JSON.parse(preferencesJStr));
                }
                result = this.preferences$.value;
            }
            catch (error)
            {
                console.error('getPreferences parse error:', error);
                result = [];
            }
        }

        return result;
    }

    /**
     * @desc   - Updates user's preferences
     * @access - public
     * @param  - preference: Object - user's prefernce
     * @return - Observable serviceObj
     */
    public updatePreference
    (
        preference: object
    )
    {
        let serviceObj: Observable<any>;
        let response: any;
        let isUpdated: boolean;

        serviceObj =
            this.apiService
            .post<any>(`${PREFERENCE.UPDATE_PREFERENCE}`,
                        preference)
            .pipe(map((result: any) =>
            {
                response = result;
                isUpdated = false;

                isUpdated =
                    response?.done === true ||
                    response?.cyData?.done === true ||
                    response?.error === ERROR.NO_ERROR;

                if (isUpdated)
                {
                    this.loadPreferences(true);
                }

                return response;
            }),
            catchError(err =>
            {
                console.log(err);

                return of(err);
            }));

        return serviceObj;
    }

    /**
     * @desc   - Retrieves the user's preferences
     * @access - public
     * @param  - moduleName: string - Name of the module
     * @return - Preference[] - User's preferences
     */
    public hasPreference
    (
        moduleName
    )
    {
        let i;
        let found;
        let preferences;

        found = false;

        preferences = this.getPreferences();

        for (i=0;i<preferences.length;i++)
        {
            if (preferences[i].name == moduleName)
            {
                found = true;
                break;
            }
        }

        return found;
    }

    /**
     * @desc   - Retrieves the user's home preferences
     * @access - public
     * @return - Preference[] - User's home preferences
     */
    public getHomePreference
    ()
    {
        let homePreference: string;
        let preferences: Preference[];
        let i: number;
        let prefCode: string;

        preferences = this.getPreferences();

        for (i = 0; i < preferences.length; i++)
        {
            prefCode = String(preferences[i].code || '').trim();

            if (prefCode === SETTING_CODE_VALUE.HOME_PAGE)
            {
                homePreference =
                    String(preferences[i].value || '').trim();
                break;
            }
        }

        return homePreference;
    }

    /**
     * @desc      - Retrieves the route tag for the home page
     * @accessess - public
     * @param     - homePreference: string - Home preference
     * @return    - string - Route tag
     */
    public getRouteTag
    (
        homePrefernce: String
    )
    {
        let routeTag;

        switch (homePrefernce)
        {
            case HOME_PAGE_VALUE.DASHBOARD:
                routeTag = HOME_TAG.CYPHER_WEBAPP;
                break;
            case HOME_PAGE_VALUE.CITATIONS:
                routeTag = HOME_TAG.CYPHER_COURT;
                break;
            case HOME_PAGE_VALUE.CASE_PROCCESS:
                routeTag = HOME_TAG.CYPHER_COURT;
                break;
            case HOME_PAGE_VALUE.CASE_QUEUE:
                routeTag = HOME_TAG.CYPHER_WEBAPP;
                break;
            case HOME_PAGE_VALUE.COURT_CALENDAR:
                routeTag = HOME_TAG.CYPHER_WEBAPP;
                break;
            case HOME_PAGE_VALUE.REPORT:
                routeTag = HOME_TAG.CYPHER_COURT;
                break;
            case HOME_PAGE_VALUE.PREFERENCE:
                routeTag = HOME_TAG.CYPHER_WEBAPP;
                break;
            case HOME_PAGE_VALUE.VIN_MODULE:
                routeTag = HOME_TAG.CYPHER_WEBAPP;
                break;
            case HOME_PAGE_VALUE.DHREPORT:
                routeTag = HOME_TAG.CYPHER_COURT;
                break;
            case HOME_PAGE_VALUE.USERS:
                routeTag = HOME_TAG.CYPHER_COURT;
                break;
            default:
                routeTag = HOME_TAG.CYPHER_WEBAPP;
        }

        return routeTag;
    }

    /**
     * @desc      - Checks if two step auth is defined
     * @access    - public
     * @param     -
     * @return    - boolean - True if two step auth is defined
     */
    public isTwoStepAuthDefined() : boolean
    {
        let isDefined: boolean;
        let preferences: Preference[];

        isDefined = false;
        preferences = this.preferences$.value || [];
    
        if (preferences && preferences.length > 0) 
        {
            preferences.find(preference => 
            {
                if (preference.code == 
                    SETTING_CODE_VALUE.MULTI_FACTOR_VERIFICATION) 
                {
                    isDefined = true;
                }
            });
        }
    
        return isDefined;
    }

    /**
     * @desc      - Checks if the user enabled two step auth
     * @access    - public
     * @param     -
     * @return    - boolean - True if two step auth is defined
     */
    public isTwoStepAuthEnabled(): boolean 
    {
        let preferences: Preference[];
        let multiFactorPref: Preference;
        let isEnabled: boolean;

        isEnabled = false;
        multiFactorPref = null;

        preferences = this.preferences$.value || [];
     
        if (preferences && preferences.length > 0) 
        {
            multiFactorPref = preferences.find(
                pref => pref.code === 
                        SETTING_CODE_VALUE.MULTI_FACTOR_VERIFICATION
            );

            if (multiFactorPref)
            {
                isEnabled = multiFactorPref.value !== 
                                TWO_STEP_VERIFICATION_VALUE.NONE;
            }
        }

        return isEnabled;
    }

    /**
     * @desc      - Checks if user enabled SSO.
     * @access    - public
     * @return    - boolean
     */
    public isSsoEnabled(): boolean
    {
        let storedPreferences: string;
        let parsedPreferences: Preference[];
        let preferences: Preference[];
        let ssoPreference: Preference;
        let enabled: boolean;

        storedPreferences = '';
        parsedPreferences = [];
        preferences = [];
        ssoPreference = null;
        enabled = false;

        storedPreferences = localStorage.getItem('preferences') || '';
        if (storedPreferences)
        {
            try
            {
                parsedPreferences = JSON.parse(storedPreferences);
                if (Array.isArray(parsedPreferences))
                {
                    preferences = parsedPreferences;
                }
            }
            catch (error)
            {
                preferences = [];
            }
        }

        ssoPreference = preferences.find(
            preference =>
                preference.code ===
                SETTING_CODE_VALUE.SSO_ENABLED
        );
        enabled =
            ssoPreference?.value === 'true';

        return enabled;
    }

    /**
     * @desc   - Get module text-case rule from user pref/system fallback
     * @access - public
     * @param  - moduleName: ModuleTextCase
     * @return - ModuleTextCaseRule
     */
    public getModuleTextCaseRule
    (
        moduleName: ModuleTextCase
    ): ModuleTextCaseRule
    {
        let rule: ModuleTextCaseRule;
        let prefValue: boolean | null;
        let capValue: boolean | null;
        let settingCode: number;
        let settingRule: ModuleTextCaseRule;

        rule = 'lower';
        prefValue = null;
        capValue = null;
        settingCode = null;
        settingRule = 'lower';

        if (moduleName === 'note')
        {
            prefValue = this.getPreferenceBool(SETTING_CODE_VALUE.NOTE_UPPERCASE);
            settingCode = SETTING_CODE_REF.NOTE_TEXT_CASE_CODE;
            settingRule = this.getSystemTextCaseRule(settingCode);
            rule = prefValue === null ? settingRule : 
                (prefValue ? 'upper' : 'lower');
        }
        else if (moduleName === 'caseMotion')
        {
            prefValue = this.getPreferenceBool(
                SETTING_CODE_VALUE.CASE_MOTION_UPPERCASE
            );
            settingCode = SETTING_CODE_REF.CASE_MOTION_TEXT_CASE_CODE;
            settingRule = this.getSystemTextCaseRule(settingCode);
            rule = prefValue === null ? settingRule :
                (prefValue ? 'upper' : 'lower');
        }
        else if (moduleName === 'probation')
        {
            prefValue = this.getPreferenceBool(
                SETTING_CODE_VALUE.PROBATION_UPPERCASE
            );
            settingCode = SETTING_CODE_REF.PROBATION_TEXT_CASE_CODE;
            settingRule = this.getSystemTextCaseRule(settingCode);
            rule = prefValue === null ? settingRule :
                (prefValue ? 'upper' : 'lower');
        }
        else if (moduleName === 'jury')
        {
            prefValue = this.getPreferenceBool(SETTING_CODE_VALUE.JURY_UPPERCASE);
            capValue = this.getPreferenceBool(
                SETTING_CODE_VALUE.JURY_CAPITALIZATION
            );

            if (prefValue === true)
            {
                rule = 'upper';
            }
            else if (capValue === true)
            {
                rule = 'capitalize';
            }
            else if (prefValue === false || capValue === false)
            {
                rule = 'lower';
            }
            else
            {
                settingCode = SETTING_CODE_REF.JURY_TEXT_CASE_CODE;
                settingRule = this.getSystemTextCaseRule(settingCode);
                rule = settingRule;
            }
        }
        else if (moduleName === 'docket')
        {
            prefValue = this.getPreferenceBool(
                SETTING_CODE_VALUE.DOCKET_UPPERCASE
            );
            settingCode = SETTING_CODE_REF.DOCKET_TEXT_CASE_CODE;
            settingRule = this.getSystemTextCaseRule(settingCode);
            rule = prefValue === null ? settingRule :
                (prefValue ? 'upper' : 'lower');
        }

        return rule;
    }

    /**
     * @desc   - Reads a boolean preference by code
     * @access - private
     * @param  - code: string
     * @return - boolean | null
     */
    private getPreferenceBool
    (
        code: string
    ): boolean | null
    {
        let preferences: Preference[];
        let matched: Preference;
        let boolValue: boolean | null;

        preferences = this.preferences$.value || [];
        matched = preferences.find((item: Preference) => item.code === code);
        boolValue = null;

        if (matched && matched.value !== undefined && matched.value !== null)
        {
            if (matched.value === 'true')
            {
                boolValue = true;
            }
            else if (matched.value === 'false')
            {
                boolValue = false;
            }
        }

        return boolValue;
    }

    /**
     * @desc   - Reads system text-case setting by code
     * @access - private
     * @param  - settingCode: number
     * @return - ModuleTextCaseRule
     */
    private getSystemTextCaseRule
    (
        settingCode: number
    ): ModuleTextCaseRule
    {
        let settingsStr: string;
        let parsed: any;
        let settings: any[];
        let match: any;
        let value: string;
        let rule: ModuleTextCaseRule;

        settingsStr = localStorage.getItem('settings');
        parsed = null;
        settings = [];
        match = null;
        value = '';
        rule = 'lower';

        if (settingsStr)
        {
            try
            {
                parsed = JSON.parse(settingsStr);
                settings = Array.isArray(parsed) ? parsed : parsed?.settings || [];
                match = settings.find((item: any) => 
                    Number(item.code) === Number(settingCode)
                );
                value = String(match?.value || '').toLowerCase();

                if (value === 'upper' || value === 'uppercase' || value === '1')
                {
                    rule = 'upper';
                }
                else
                {
                    rule = 'lower';
                }
            }
            catch (error)
            {
                rule = 'lower';
            }
        }

        return rule;
    }
}    

export { PreferenceService };
